import React, { useContext, ReactElement, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import {
  AndiUserProfile,
  BookedSeat,
  BusinessUnit,
  GroupBooking,
  GroupMember
} from '../../types/andTypes'
import SeatPickerKey from './seat-picker-key'
import { ModalContext } from '../../contexts/modal-context'
import { LoginContext } from '../../contexts/login-context'
import { ApiContext } from '../../contexts/api-context'
import DecisionPopup from '../modal/popup/decision-popup/decision-popup'
import ResultPopup from '../modal/popup/result-popup/result-popup'
import LoadingPopup from '../modal/popup/loading-popup/loading-popup'
import GroupBookingPopup from '../modal/popup/group-booking-popup/group-booking-popup'
import {
  OccupiedSeat,
  SeatPickerGrid,
  AvailableSeat,
  PlusIcon,
  HorizontalLine,
  VerticalLine,
  HoverText,
  ErrorText
} from './seat-picker-styles'
import { generateIcsFile, getUserId } from '../../utilities/utils'
import logAnalyticsEvent from '../../utilities/firebase-analytics'
import BookingTypePopup from '../modal/popup/booking-type-popup/booking-type-popup'
import { v4 as uuid } from 'uuid'
import { ErrorMessages } from '../../constants'

type SeatPickerProps = {
  bookedAndis: BookedSeat[] | []
  bookedAndisForAllBu: BookedSeat[] | []
  isUserBooked: boolean
  selectedDay: Date
  selectedBU: BusinessUnit
  isBankHoliday: boolean
}

const SeatPicker: React.FC<SeatPickerProps> = ({
  bookedAndis,
  bookedAndisForAllBu,
  isUserBooked,
  selectedDay,
  selectedBU,
  isBankHoliday
}) => {
  const { setModalOpen, setModalContent } = useContext(ModalContext)
  const dateString = dayjs(selectedDay).format('YYYY-MM-DD')
  const { user } = useContext(LoginContext)
  const { apiClient, userProfile, getANDis, getTeams } = useContext(ApiContext)
  const [BuANDis, setBuANDis] = useState<AndiUserProfile[]>([])
  const selectedLocation = selectedBU.name + ', ' + selectedBU.location

  useEffect(() => {
    setBuANDis(getANDis)
  }, [])

  const openBookingTypeModal = () => {
    setModalOpen(true)
    setModalContent(
      <BookingTypePopup
        header="Booking Type"
        singleSelectedAction={() => {
          openSingleBookingDecisionModal()
        }}
        groupSelectedAction={() => {
          openGroupBookingModal()
        }}
        availableSeats={selectedBU.deskCapacity - bookedAndis.length}
      />
    )
  }
  const openSingleBookingDecisionModal = () => {
    setModalOpen(true)
    logAnalyticsEvent('booking_decision', {
      user: user?.email,
      team: userProfile?.team,
      booking_location: selectedBU.id,
      default_location: userProfile?.businessUnit
    })
    setModalContent(
      <DecisionPopup
        header="Booking Details"
        bookingDetails={{ date: dateString, clubId: selectedBU.id }}
        confirmAction={confirmBooking}
      />
    )
  }

  const openGroupBookingModal = () => {
    setModalOpen(true)
    setModalContent(
      <GroupBookingPopup
        type="open"
        header="Group"
        dateString={dateString}
        selectedLocation={selectedLocation}
        confirmGroupAction={confirmGroupBooking}
        ANDis={BuANDis}
        organiser={userProfile}
        availableSeats={selectedBU.deskCapacity - bookedAndis.length}
        teams={getTeams(selectedBU.id, userProfile?.businessUnit || '')}
        bookedAndis={bookedAndisForAllBu}
        selectedBU={selectedBU}
      />
    )
  }

  const amendGroupBookingModal = (selectedMembers: AndiUserProfile[]) => {
    setModalContent(
      <GroupBookingPopup
        type="amend"
        header="Group"
        dateString={dateString}
        selectedLocation={selectedLocation}
        confirmGroupAction={confirmGroupBooking}
        ANDis={BuANDis}
        organiser={userProfile}
        selectedANDis={selectedMembers}
        teams={getTeams(selectedBU.id, userProfile?.businessUnit || '')}
        selectedBU={selectedBU}
      />
    )
  }

  const confirmBooking = async () => {
    if (!user?.email || !selectedBU) return

    const userId = getUserId(user.email)
    const userName = user.displayName || userId

    setModalContent(<LoadingPopup />)

    const startTime = new Date().getTime()
    try {
      if (selectedDay < new Date() && selectedDay.toDateString() !== new Date().toDateString()) {
        // eslint-disable-next-line no-console
        console.info('error booking selected day %s', dateString)
        throw new Error('This date is no longer valid for bookings')
      }
      await apiClient.createUserBooking({
        userId,
        name: userName,
        businessUnit: selectedBU.id,
        dateBooked: new Date(selectedDay.getTime())
      })
      logAnalyticsEvent('booking_creation_success', {
        latency_ms: new Date().getTime() - startTime,
        user: user?.email,
        team: userProfile?.team,
        booking_location: selectedBU.id,
        default_location: userProfile?.businessUnit
      })
      setModalContent(
        <ResultPopup
          headerText="Yay! Booking Confirmed"
          success={true}
          actionButtonConfig={{
            text: 'Download booking',
            isCircle: false,
            onClick: () => generateIcsFile(dateString, selectedBU)
          }}
        >
          <p>
            Your desk is booked and confirmed.
            <br />
            Download ICS file to add your booking to your calendar.
          </p>
        </ResultPopup>
      )
    } catch (error) {
      let message = ''
      logAnalyticsEvent('booking_creation_error', {
        latency_ms: new Date().getTime() - startTime,
        user: user?.email,
        team: userProfile?.team,
        booking_location: selectedBU.id,
        default_location: userProfile?.businessUnit
      })
      if (error instanceof Error) {
        message = error.message
      } else {
        message = 'Something went wrong. Please try to book your desk again.'
      }
      setModalContent(
        <ResultPopup headerText="Oh no!" success={false}>
          <p>{message}</p>
        </ResultPopup>
      )
    }
  }

  const confirmGroupBooking = async (members: AndiUserProfile[]) => {
    if (!user?.email || !selectedBU) return

    const userId = getUserId(user.email)
    setModalContent(<LoadingPopup />)

    const bookingDetails: GroupBooking = {
      id: uuid(),
      bookingUserId: userId,
      businessUnit: selectedBU.id,
      dateBooked: new Date(selectedDay.getTime()),
      members: members.map(({ name, userId }) => ({ name, userId })) as GroupMember[]
    }

    const startTime = new Date().getTime()
    try {
      await apiClient.createGroupBooking(bookingDetails)
      logAnalyticsEvent('group_booking_creation_success', {
        latency_ms: new Date().getTime() - startTime,
        user: user?.email,
        team: userProfile?.team,
        booking_location: selectedBU.id,
        default_location: userProfile?.businessUnit
      })
      setModalContent(
        <ResultPopup
          headerText="Yay! Booking Confirmed"
          success={true}
          actionButtonConfig={{
            text: 'Download booking',
            isCircle: false,
            onClick: () => generateIcsFile(dateString, selectedBU)
          }}
        >
          <p>
            Your group booking is confirmed.
            <br />
            Download ICS file to add your booking to your calendar.
          </p>
        </ResultPopup>
      )
    } catch (error) {
      let message = ''
      logAnalyticsEvent('group_booking_creation_error', {
        latency_ms: new Date().getTime() - startTime,
        user: user?.email,
        team: userProfile?.team,
        booking_location: selectedBU.id,
        default_location: userProfile?.businessUnit
      })
      if (error instanceof Error) {
        // eslint-disable-next-line no-console
        console.log('error msg', error.message)
        message = error.message
      } else {
        message = 'Something went wrong. Please try to book your desk again.'
      }

      if (message === ErrorMessages.NO_SEATS_AVAILABLE) {
        setModalContent(
          <ResultPopup headerText="Oh no!" success={false}>
            <p>{message}</p>
          </ResultPopup>
        )

        return
      }

      setModalContent(
        <ResultPopup
          actionButtonConfig={{
            text: 'Amend booking',
            isPrimary: true,
            isCircle: false,
            onClick: () => amendGroupBookingModal(members)
          }}
          headerText={'Oh no!'}
          success={false}
        >
          <ErrorText>
            Unfortunately your group booking was unsuccessful:{'\n'}
            {message}
          </ErrorText>
        </ResultPopup>
      )
    }
  }

  const generateSeatIconArray = (bookedAndis: BookedSeat[]) => {
    const iconArray: ReactElement[] = []
    bookedAndis.map((andi: BookedSeat) =>
      iconArray.push(
        <OccupiedSeat key={andi.userId} role="img" alt={`Occupied Desk: ${andi.name}`}>
          {andi.initials}
          <HoverText>{andi.name}</HoverText>
        </OccupiedSeat>
      )
    )
    if (iconArray.length < selectedBU.deskCapacity) {
      iconArray.push(
        <AvailableSeat
          key="add-button"
          role="button"
          title="Book Desk button"
          isUserBooked={isUserBooked}
          isBankHoliday={isBankHoliday}
          onClick={() => !isUserBooked && !isBankHoliday && openBookingTypeModal()}
        >
          <PlusIcon
            isUserBooked={isUserBooked || !userProfile?.businessUnit}
            isBankHoliday={isBankHoliday}
          >
            <HorizontalLine />
            <VerticalLine />
          </PlusIcon>
          <HoverText>
            {isUserBooked && !isBankHoliday
              ? 'You already have a booking on this date'
              : isBankHoliday
              ? 'Today is a bank holiday'
              : 'Click to book a desk'}
          </HoverText>
        </AvailableSeat>
      )
    }
    for (let i = iconArray.length; i < selectedBU.deskCapacity; i++) {
      iconArray.push(<AvailableSeat key={`available-${i}`} alt="Available Desk" role="avaiable" />)
    }
    return iconArray
  }

  const generatedSeats = generateSeatIconArray(bookedAndis)

  return (
    <>
      <SeatPickerKey />
      <SeatPickerGrid>{generatedSeats}</SeatPickerGrid>
    </>
  )
}

export default SeatPicker
